import React, { FC, memo, useCallback, useEffect, useState } from "react";
import { FormikProps, withFormik } from "formik";
import { List } from "immutable";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { DestinationAddressResponseDto } from "types/api/destination_addresses";
import { BooksRecipientsProps } from "types/books";
import AddAddressButton from "../../components/Addresses/components/AddAddressButton";
import LoadingBar from "../../components/LoadingBar/LoadingBar";
import injectReducer from "../../reduxFolder/injectReducer";
import booksReducer from "../../reduxFolder/reducers/books";
import { getAddresses } from "../../reduxFolder/reducers/destinations";
import {
  destinationsSelectAddresses,
  destinationsSelectLoading,
} from "../../reduxFolder/selectors/destinationSelectors";
import { panelSelectOpened } from "../../reduxFolder/selectors/rightPanelSelectors";
import BooksRecipientsList from "./components/BooksRecipientsList/BooksRecipientsList";
import BooksSearchPlaceholder from "./components/BooksRecipientsList/BooksSearchPlaceholder";
import BooksSearchRecipients from "./components/BooksRecipientsList/BooksSearchRecipients";
import ImportAddressesButton from "./components/BooksRecipientsList/ImportAddressesButton";
import { searchRecipientsFormik } from "./helpers/searchRecipientsFormik";
import {
  AddAddressBlock,
  RecipientsHeading,
  StyledGrid,
  Wrapper,
} from "./styles/BookRecipients.style";

// How long a just-added address stays highlighted in the list.
const ADDED_HIGHLIGHT_MS = 4000;

const BooksRecipients: FC<BooksRecipientsProps> = React.memo(
  ({
    recipientsItems,
    isLoading,
    getAddresses,
    isPanelOpened,
    handleReset,
    handleSubmit,
    setFieldValue,
    initialValues,
    values,
  }) => {
    const hasItems = !!recipientsItems.size;
    // An address added from the panel lands at the end of the list: its row is
    // scrolled into view and highlighted for a moment.
    const [addedAddressId, setAddedAddressId] = useState<number | null>(null);

    useEffect(() => {
      getAddresses();
    }, [getAddresses]);

    useEffect(() => {
      if (addedAddressId === null) return;
      const timer = setTimeout(
        () => setAddedAddressId(null),
        ADDED_HIGHLIGHT_MS,
      );
      return () => clearTimeout(timer);
    }, [addedAddressId]);

    const handleAddressAdded = useCallback(
      (address: DestinationAddressResponseDto) => setAddedAddressId(address.id),
      [],
    );

    const handleResetClick = useCallback(() => {
      handleReset();
      handleSubmit();
    }, [handleReset, handleSubmit]);

    return (
      <Wrapper>
        <StyledGrid fluid>
          <LoadingBar isLoading={isLoading} />
          <RecipientsHeading $isPanelOpened={isPanelOpened}>
            <BooksSearchRecipients
              handleSubmit={handleSubmit}
              setFieldValue={setFieldValue}
              values={values}
              initialValues={initialValues}
            />
            <AddAddressBlock>
              <ImportAddressesButton />
              <AddAddressButton onAddressAdded={handleAddressAdded} />
            </AddAddressBlock>
          </RecipientsHeading>

          {hasItems ? (
            <BooksRecipientsList
              recipientsItems={recipientsItems}
              disabled={isLoading}
              addedAddressId={addedAddressId}
            />
          ) : (
            !isLoading && (
              <BooksSearchPlaceholder handleReset={handleResetClick} />
            )
          )}
        </StyledGrid>
      </Wrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    recipientsItems: destinationsSelectAddresses,
    isLoading: destinationsSelectLoading,
    isPanelOpened: panelSelectOpened,
  }),
  {
    getAddresses,
  },
);

const withBooksReducer = injectReducer({
  key: "books",
  reducer: booksReducer,
});

const enhance = compose(
  withBooksReducer,
  withConnect,
  withFormik(searchRecipientsFormik),
);

export default enhance(BooksRecipients);
